import { Button } from '@kontentino/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

export type MetaConnectionMethod = 'obo' | 'personal';

interface MetaConnectionMethodCardsProps {
  onSelectBusinessManager: () => void;
  onSelectPageAdmin: () => void;
  /** The method being started. Both buttons are disabled while it is set. */
  loading?: MetaConnectionMethod | null;
  dataCy?: { obo: string; personal: string };
}

const DEFAULT_DATA_CY = {
  obo: 'add-profile_fb-token-type_obo',
  personal: 'add-profile_fb-token-type_personal',
};

interface MethodCardProps {
  title: string;
  points: string[];
  buttonLabel: string;
  buttonDataCy: string;
  isLoading: boolean;
  disabled: boolean;
  onSelect: () => void;
}

const MethodCard = ({
  title,
  points,
  buttonLabel,
  buttonDataCy,
  isLoading,
  disabled,
  onSelect,
}: MethodCardProps) => {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className="tw-rounded-md tw-bg-grayscale-5 tw-p-4"
    >
      <h3
        id={titleId}
        className="tw-text-base tw-font-semibold tw-text-grayscale-180"
      >
        {title}
      </h3>
      <p className="tw-mt-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-grayscale-140">
        {t('newSocialMediaProfile.methodCards.whatToExpect')}
      </p>
      <ul className="tw-mt-2 tw-list-disc tw-pl-5 tw-text-md tw-text-grayscale-140">
        {points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
      <Button
        variant="primary"
        size="medium"
        className="tw-mt-4 tw-w-full"
        isLoading={isLoading}
        disabled={disabled}
        aria-busy={isLoading || undefined}
        data-cy={buttonDataCy}
        onClick={onSelect}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

const MetaConnectionMethodCards = ({
  onSelectBusinessManager,
  onSelectPageAdmin,
  loading = null,
  dataCy = DEFAULT_DATA_CY,
}: MetaConnectionMethodCardsProps) => {
  const { t } = useTranslation();
  const isBusy = loading !== null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <MethodCard
        title={t('newSocialMediaProfile.methodCards.businessManagerTitle')}
        points={[
          t('newSocialMediaProfile.methodCards.businessManagerPoints.1'),
          t('newSocialMediaProfile.methodCards.businessManagerPoints.2'),
          t('newSocialMediaProfile.methodCards.businessManagerPoints.3'),
        ]}
        buttonLabel={t(
          'newSocialMediaProfile.methodCards.continueBusinessManager',
        )}
        buttonDataCy={dataCy.obo}
        isLoading={loading === 'obo'}
        disabled={isBusy}
        onSelect={onSelectBusinessManager}
      />
      <MethodCard
        title={t('newSocialMediaProfile.methodCards.pageAdminTitle')}
        points={[
          t('newSocialMediaProfile.methodCards.pageAdminPoints.1'),
          t('newSocialMediaProfile.methodCards.pageAdminPoints.2'),
          t('newSocialMediaProfile.methodCards.pageAdminPoints.3'),
        ]}
        buttonLabel={t('newSocialMediaProfile.methodCards.continueFacebook')}
        buttonDataCy={dataCy.personal}
        isLoading={loading === 'personal'}
        disabled={isBusy}
        onSelect={onSelectPageAdmin}
      />
    </div>
  );
};

export default MetaConnectionMethodCards;
